import * as THREE from 'three'

/**
 * Modelos procedurais 3D de animais de grande porte (500 kg) para simulação
 * de salvamento terrestre e içamento em altura (bombeiros).
 *
 * Suporta posturas:
 *  - 0: em pé (preso em poço, vala estreita, cisterna);
 *  - 1: deitado/caído (em decúbito lateral em ribanceira, atoleiro ou fundo de vala).
 *
 * As coordenadas locais dos componentes alinham-se exatamente com os pontos
 * de ancoragem definidos em `dominio/tabletop/ancoragem.ts`:
 * `dorso` (olhal de içamento), `peitoral`, `garupa` e `cabeca`.
 */

const COR_FITA_RESGATE = '#f97316' // Laranja de alta visibilidade
const COR_ARGOLA_ACO = '#cbd5e1'
const COR_CASCO = '#1e293b'

function ArgolaDorsal({ posicao }: { posicao: [number, number, number] }) {
  return (
    <group position={posicao}>
      {/* Olhal de aço para conexão do mosquetão ou gancho de içamento */}
      <mesh rotation={[Math.PI / 2, 0, 0]} renderOrder={12}>
        <torusGeometry args={[0.07, 0.018, 12, 24]} />
        <meshStandardMaterial color={COR_ARGOLA_ACO} metalness={0.85} roughness={0.25} />
      </mesh>
    </group>
  )
}

function AreaDeToqueAnimal({ altura, raio }: { altura: number; raio: number }) {
  return (
    <mesh position={[0, altura, 0]}>
      <cylinderGeometry args={[raio, raio, altura * 2, 8]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  )
}

export function CavaloTabletop({ postura = 0 }: { postura?: number }) {
  const deitado = postura === 1
  const corPelagem = '#7c2d12' // Castanho alazão
  const corCrina = '#291e18' // Escuro

  if (deitado) {
    return (
      <group name="cavalo-deitado">
        <AreaDeToqueAnimal altura={0.4} raio={1.2} />

        {/* Tronco deitado sobre o flanco */}
        <mesh position={[0, 0.35, 0]} rotation={[0, 0, Math.PI / 2]}>
          <capsuleGeometry args={[0.38, 1.2, 12, 16]} />
          <meshStandardMaterial color={corPelagem} roughness={0.8} />
        </mesh>

        {/* Garupa e Anca */}
        <mesh position={[0, 0.38, -0.6]}>
          <sphereGeometry args={[0.42, 12, 10]} />
          <meshStandardMaterial color={corPelagem} roughness={0.8} />
        </mesh>

        {/* Peitoral */}
        <mesh position={[0, 0.35, 0.55]}>
          <sphereGeometry args={[0.38, 12, 10]} />
          <meshStandardMaterial color={corPelagem} roughness={0.8} />
        </mesh>

        {/* Pescoço reclinado */}
        <mesh position={[0, 0.32, 0.85]} rotation={[0.4, 0, 0]}>
          <cylinderGeometry args={[0.22, 0.32, 0.6, 12]} />
          <meshStandardMaterial color={corPelagem} roughness={0.8} />
        </mesh>

        {/* Cabeça */}
        <mesh position={[0, 0.28, 1.15]} rotation={[0.2, 0, 0]}>
          <boxGeometry args={[0.24, 0.26, 0.42]} />
          <meshStandardMaterial color={corPelagem} roughness={0.8} />
        </mesh>

        {/* Orelhas */}
        <mesh position={[-0.08, 0.45, 1.05]} rotation={[-0.3, -0.2, 0]}>
          <coneGeometry args={[0.045, 0.14, 6]} />
          <meshStandardMaterial color={corPelagem} roughness={0.8} />
        </mesh>
        <mesh position={[0.08, 0.45, 1.05]} rotation={[-0.3, 0.2, 0]}>
          <coneGeometry args={[0.045, 0.14, 6]} />
          <meshStandardMaterial color={corPelagem} roughness={0.8} />
        </mesh>

        {/* Patas dobradas de lado */}
        {[-0.45, 0.45].map((z, i) => (
          <group key={i}>
            <mesh position={[0.4, 0.18, z]} rotation={[0, 0, 1.2]}>
              <cylinderGeometry args={[0.09, 0.08, 0.65, 8]} />
              <meshStandardMaterial color={corPelagem} roughness={0.8} />
            </mesh>
            <mesh position={[0.7, 0.12, z + 0.1]} rotation={[0, 0, 0.4]}>
              <cylinderGeometry args={[0.075, 0.065, 0.55, 8]} />
              <meshStandardMaterial color={corPelagem} roughness={0.8} />
            </mesh>
            <mesh position={[0.85, 0.1, z + 0.15]}>
              <boxGeometry args={[0.1, 0.08, 0.12]} />
              <meshStandardMaterial color={COR_CASCO} roughness={0.9} />
            </mesh>
          </group>
        ))}

        {/* Fitas do arreio de salvamento */}
        <mesh position={[0, 0.36, 0.35]} rotation={[0, 0, Math.PI / 2]}>
          <torusGeometry args={[0.4, 0.035, 8, 24]} />
          <meshStandardMaterial color={COR_FITA_RESGATE} roughness={0.7} />
        </mesh>
        <mesh position={[0, 0.38, -0.35]} rotation={[0, 0, Math.PI / 2]}>
          <torusGeometry args={[0.42, 0.035, 8, 24]} />
          <meshStandardMaterial color={COR_FITA_RESGATE} roughness={0.7} />
        </mesh>

        {/* Olhal dorsal */}
        <ArgolaDorsal posicao={[0, 0.55, 0]} />
      </group>
    )
  }

  // Postura em pé
  return (
    <group name="cavalo-em-pe">
      <AreaDeToqueAnimal altura={1.0} raio={1.1} />

      {/* Tronco principal */}
      <mesh position={[0, 1.15, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <capsuleGeometry args={[0.38, 1.1, 12, 16]} />
        <meshStandardMaterial color={corPelagem} roughness={0.8} />
      </mesh>

      {/* Garupa musculosa */}
      <mesh position={[0, 1.25, -0.6]}>
        <sphereGeometry args={[0.42, 12, 12]} />
        <meshStandardMaterial color={corPelagem} roughness={0.8} />
      </mesh>

      {/* Peitoral largo */}
      <mesh position={[0, 1.15, 0.55]}>
        <sphereGeometry args={[0.4, 12, 12]} />
        <meshStandardMaterial color={corPelagem} roughness={0.8} />
      </mesh>

      {/* Pescoço empinado */}
      <mesh position={[0, 1.45, 0.65]} rotation={[-0.55, 0, 0]}>
        <cylinderGeometry args={[0.22, 0.34, 0.75, 12]} />
        <meshStandardMaterial color={corPelagem} roughness={0.8} />
      </mesh>

      {/* Crina */}
      <mesh position={[0, 1.58, 0.58]} rotation={[-0.55, 0, 0]}>
        <boxGeometry args={[0.07, 0.78, 0.12]} />
        <meshStandardMaterial color={corCrina} roughness={0.9} />
      </mesh>

      {/* Cabeça */}
      <mesh position={[0, 1.72, 0.95]} rotation={[-0.25, 0, 0]}>
        <boxGeometry args={[0.22, 0.28, 0.44]} />
        <meshStandardMaterial color={corPelagem} roughness={0.8} />
      </mesh>

      {/* Focinho */}
      <mesh position={[0, 1.62, 1.15]} rotation={[-0.2, 0, 0]}>
        <boxGeometry args={[0.18, 0.2, 0.2]} />
        <meshStandardMaterial color="#3b1d11" roughness={0.85} />
      </mesh>

      {/* Orelhas */}
      <mesh position={[-0.08, 1.9, 0.88]} rotation={[0.2, -0.2, 0]}>
        <coneGeometry args={[0.045, 0.15, 6]} />
        <meshStandardMaterial color={corPelagem} roughness={0.8} />
      </mesh>
      <mesh position={[0.08, 1.9, 0.88]} rotation={[0.2, 0.2, 0]}>
        <coneGeometry args={[0.045, 0.15, 6]} />
        <meshStandardMaterial color={corPelagem} roughness={0.8} />
      </mesh>

      {/* Cauda */}
      <mesh position={[0, 1.05, -0.92]} rotation={[0.3, 0, 0]}>
        <cylinderGeometry args={[0.07, 0.03, 0.75, 8]} />
        <meshStandardMaterial color={corCrina} roughness={0.9} />
      </mesh>

      {/* 4 Patas robustas */}
      {[
        [-0.26, 0.5], // Dianteira esquerda
        [0.26, 0.5],  // Dianteira direita
        [-0.26, -0.6], // Traseira esquerda
        [0.26, -0.6],  // Traseira direita
      ].map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          {/* Coxa/Canela */}
          <mesh position={[0, 0.55, 0]}>
            <cylinderGeometry args={[0.09, 0.065, 1.1, 8]} />
            <meshStandardMaterial color={corPelagem} roughness={0.8} />
          </mesh>
          {/* Casco */}
          <mesh position={[0, 0.05, 0.02]}>
            <cylinderGeometry args={[0.075, 0.085, 0.1, 8]} />
            <meshStandardMaterial color={COR_CASCO} roughness={0.9} />
          </mesh>
        </group>
      ))}

      {/* Fitas do arreio de içamento (peitoral e barrigueira/garupa) */}
      <mesh position={[0, 1.15, 0.35]}>
        <torusGeometry args={[0.42, 0.035, 8, 24]} />
        <meshStandardMaterial color={COR_FITA_RESGATE} roughness={0.7} />
      </mesh>
      <mesh position={[0, 1.2, -0.35]}>
        <torusGeometry args={[0.43, 0.035, 8, 24]} />
        <meshStandardMaterial color={COR_FITA_RESGATE} roughness={0.7} />
      </mesh>

      {/* Tirante de união dorsal ao olhal */}
      <mesh position={[0, 1.45, 0]}>
        <boxGeometry args={[0.08, 0.15, 0.7]} />
        <meshStandardMaterial color={COR_FITA_RESGATE} roughness={0.7} />
      </mesh>

      {/* Olhal dorsal */}
      <ArgolaDorsal posicao={[0, 1.55, 0]} />
    </group>
  )
}

export function VacaTabletop({ postura = 0 }: { postura?: number }) {
  const deitado = postura === 1
  const corBranco = '#f8fafc'
  const corMancha = '#1e293b' // Manchas pretas holandesa
  const corFocinho = '#fca5a5' // Rosa claro
  const corChifre = '#cbd5e1'

  if (deitado) {
    return (
      <group name="vaca-deitada">
        <AreaDeToqueAnimal altura={0.4} raio={1.3} />

        {/* Tronco largo deitado */}
        <mesh position={[0, 0.35, 0]} rotation={[0, 0, Math.PI / 2]}>
          <capsuleGeometry args={[0.44, 1.2, 12, 16]} />
          <meshStandardMaterial color={corBranco} roughness={0.8} />
        </mesh>

        {/* Manchas corporais */}
        <mesh position={[0.1, 0.42, 0.1]} rotation={[0.2, 0.4, 0]}>
          <sphereGeometry args={[0.35, 8, 8]} />
          <meshStandardMaterial color={corMancha} roughness={0.85} />
        </mesh>
        <mesh position={[-0.1, 0.4, -0.4]} rotation={[0, -0.3, 0]}>
          <sphereGeometry args={[0.32, 8, 8]} />
          <meshStandardMaterial color={corMancha} roughness={0.85} />
        </mesh>

        {/* Peitoral e pescoço curto */}
        <mesh position={[0, 0.32, 0.65]}>
          <sphereGeometry args={[0.42, 12, 10]} />
          <meshStandardMaterial color={corBranco} roughness={0.8} />
        </mesh>

        {/* Cabeça bovina */}
        <mesh position={[0, 0.28, 1.05]} rotation={[0.15, 0, 0]}>
          <boxGeometry args={[0.3, 0.3, 0.38]} />
          <meshStandardMaterial color={corBranco} roughness={0.8} />
        </mesh>

        {/* Espelho nasal / focinho largo */}
        <mesh position={[0, 0.22, 1.25]} rotation={[0.1, 0, 0]}>
          <boxGeometry args={[0.26, 0.18, 0.16]} />
          <meshStandardMaterial color={corFocinho} roughness={0.7} />
        </mesh>

        {/* Chifres */}
        <mesh position={[-0.14, 0.42, 1.02]} rotation={[0.4, -0.4, 0.5]}>
          <coneGeometry args={[0.035, 0.15, 8]} />
          <meshStandardMaterial color={corChifre} roughness={0.6} />
        </mesh>
        <mesh position={[0.14, 0.42, 1.02]} rotation={[0.4, 0.4, -0.5]}>
          <coneGeometry args={[0.035, 0.15, 8]} />
          <meshStandardMaterial color={corChifre} roughness={0.6} />
        </mesh>

        {/* Patas deitadas */}
        {[-0.45, 0.45].map((z, i) => (
          <group key={i}>
            <mesh position={[0.4, 0.18, z]} rotation={[0, 0, 1.2]}>
              <cylinderGeometry args={[0.1, 0.09, 0.6, 8]} />
              <meshStandardMaterial color={corBranco} roughness={0.8} />
            </mesh>
            <mesh position={[0.7, 0.1, z + 0.1]}>
              <boxGeometry args={[0.12, 0.09, 0.14]} />
              <meshStandardMaterial color={COR_CASCO} roughness={0.9} />
            </mesh>
          </group>
        ))}

        {/* Fitas do arreio de içamento */}
        <mesh position={[0, 0.36, 0.35]} rotation={[0, 0, Math.PI / 2]}>
          <torusGeometry args={[0.46, 0.035, 8, 24]} />
          <meshStandardMaterial color={COR_FITA_RESGATE} roughness={0.7} />
        </mesh>
        <mesh position={[0, 0.38, -0.35]} rotation={[0, 0, Math.PI / 2]}>
          <torusGeometry args={[0.46, 0.035, 8, 24]} />
          <meshStandardMaterial color={COR_FITA_RESGATE} roughness={0.7} />
        </mesh>

        {/* Olhal dorsal */}
        <ArgolaDorsal posicao={[0, 0.55, 0]} />
      </group>
    )
  }

  // Vaca em pé
  return (
    <group name="vaca-em-pe">
      <AreaDeToqueAnimal altura={0.95} raio={1.15} />

      {/* Tronco largo e robusto */}
      <mesh position={[0, 1.1, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <capsuleGeometry args={[0.44, 1.1, 12, 16]} />
        <meshStandardMaterial color={corBranco} roughness={0.8} />
      </mesh>

      {/* Manchas corporais pretas */}
      <mesh position={[0.2, 1.25, 0.1]}>
        <sphereGeometry args={[0.38, 8, 8]} />
        <meshStandardMaterial color={corMancha} roughness={0.85} />
      </mesh>
      <mesh position={[-0.2, 1.18, -0.4]}>
        <sphereGeometry args={[0.36, 8, 8]} />
        <meshStandardMaterial color={corMancha} roughness={0.85} />
      </mesh>
      <mesh position={[0.15, 1.15, -0.55]}>
        <sphereGeometry args={[0.28, 8, 8]} />
        <meshStandardMaterial color={corMancha} roughness={0.85} />
      </mesh>

      {/* Garupa reta bovina */}
      <mesh position={[0, 1.2, -0.6]}>
        <boxGeometry args={[0.76, 0.65, 0.5]} />
        <meshStandardMaterial color={corBranco} roughness={0.8} />
      </mesh>

      {/* Peitoral amplo */}
      <mesh position={[0, 1.1, 0.55]}>
        <boxGeometry args={[0.74, 0.7, 0.45]} />
        <meshStandardMaterial color={corBranco} roughness={0.8} />
      </mesh>

      {/* Pescoço curto */}
      <mesh position={[0, 1.28, 0.72]} rotation={[-0.35, 0, 0]}>
        <cylinderGeometry args={[0.28, 0.38, 0.55, 12]} />
        <meshStandardMaterial color={corBranco} roughness={0.8} />
      </mesh>

      {/* Cabeça bovina */}
      <mesh position={[0, 1.38, 0.96]} rotation={[-0.15, 0, 0]}>
        <boxGeometry args={[0.3, 0.32, 0.4]} />
        <meshStandardMaterial color={corBranco} roughness={0.8} />
      </mesh>

      {/* Focinho largo rosa */}
      <mesh position={[0, 1.26, 1.16]} rotation={[-0.1, 0, 0]}>
        <boxGeometry args={[0.26, 0.18, 0.18]} />
        <meshStandardMaterial color={corFocinho} roughness={0.7} />
      </mesh>

      {/* Chifres */}
      <mesh position={[-0.16, 1.56, 0.92]} rotation={[0.3, -0.4, 0.6]}>
        <coneGeometry args={[0.035, 0.16, 8]} />
        <meshStandardMaterial color={corChifre} roughness={0.6} />
      </mesh>
      <mesh position={[0.16, 1.56, 0.92]} rotation={[0.3, 0.4, -0.6]}>
        <coneGeometry args={[0.035, 0.16, 8]} />
        <meshStandardMaterial color={corChifre} roughness={0.6} />
      </mesh>

      {/* Cauda fina com tufo */}
      <mesh position={[0, 0.9, -0.88]}>
        <cylinderGeometry args={[0.025, 0.025, 0.75, 6]} />
        <meshStandardMaterial color={corBranco} roughness={0.8} />
      </mesh>
      <mesh position={[0, 0.5, -0.88]}>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshStandardMaterial color={corMancha} roughness={0.85} />
      </mesh>

      {/* 4 Patas sólidas */}
      {[
        [-0.26, 0.5],
        [0.26, 0.5],
        [-0.26, -0.6],
        [0.26, -0.6],
      ].map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          <mesh position={[0, 0.5, 0]}>
            <cylinderGeometry args={[0.1, 0.08, 1.0, 8]} />
            <meshStandardMaterial color={corBranco} roughness={0.8} />
          </mesh>
          <mesh position={[0, 0.05, 0.02]}>
            <boxGeometry args={[0.12, 0.1, 0.14]} />
            <meshStandardMaterial color={COR_CASCO} roughness={0.9} />
          </mesh>
        </group>
      ))}

      {/* Fitas de içamento do arreio */}
      <mesh position={[0, 1.1, 0.35]}>
        <torusGeometry args={[0.48, 0.035, 8, 24]} />
        <meshStandardMaterial color={COR_FITA_RESGATE} roughness={0.7} />
      </mesh>
      <mesh position={[0, 1.15, -0.35]}>
        <torusGeometry args={[0.48, 0.035, 8, 24]} />
        <meshStandardMaterial color={COR_FITA_RESGATE} roughness={0.7} />
      </mesh>
      <mesh position={[0, 1.45, 0]}>
        <boxGeometry args={[0.08, 0.15, 0.7]} />
        <meshStandardMaterial color={COR_FITA_RESGATE} roughness={0.7} />
      </mesh>

      {/* Olhal dorsal de içamento */}
      <ArgolaDorsal posicao={[0, 1.55, 0]} />
    </group>
  )
}

